import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, ChevronLeft, ChevronRight, MessageSquare, Bot } from 'lucide-react'

const WorkoutReflections = ({ onBack, currentUser }) => {
  const { t } = useTranslation()
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentDate, setCurrentDate] = useState(new Date())

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const response = await fetch('/api/v1/workout_logs')
        if (response.ok) {
          const data = await response.json()
          setLogs(data)
        }
      } catch (err) {
        console.error('Failed to fetch logs', err)
      } finally {
        setLoading(false)
      }
    }
    fetchLogs()
  }, [])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const monthlyLogs = useMemo(() => {
    if (logs.length === 0) return []
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth()
    return logs
      .filter(log => {
        const logDate = new Date(log.date)
        return logDate.getFullYear() === year && logDate.getMonth() === month
      })
      .filter(log => log.note || log.ai_advice)
      .sort((a, b) => new Date(a.date) - new Date(b.date))
  }, [logs, currentDate])

  const nextMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))
  }

  const prevMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))
  }

  const formatMonth = (date) => {
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long' })
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-gray-400">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500 mb-4"></div>
        <p>{t('loading')}</p>
      </div>
    )
  }

  return (
    <div className="w-full min-h-screen bg-[#111] overflow-x-hidden">
      <div className="sticky top-0 z-40 bg-[#111] px-4 pt-6 pb-2 border-b border-gray-800 shadow-xl">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors text-gray-400 hover:text-white"
            >
              <ArrowLeft size={24} />
            </button>
            <h1 className="text-2xl font-bold text-white leading-none">{t('reflections_title')}</h1>
          </div>

          <div className="flex items-center justify-between bg-[#1c1c1e] p-4 rounded-xl border border-gray-800 mb-4">
            <button 
              onClick={prevMonth} 
              className="p-2 hover:bg-[#2c2c2e] rounded-lg text-gray-400 hover:text-white transition-colors"
            >
              <ChevronLeft size={24} />
            </button>
            <span className="text-lg font-bold text-orange-500">{formatMonth(currentDate)}</span>
            <button 
              onClick={nextMonth} 
              className="p-2 hover:bg-[#2c2c2e] rounded-lg text-gray-400 hover:text-white transition-colors"
            >
              <ChevronRight size={24} />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8 pb-32 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
        {monthlyLogs.length > 0 ? (
          monthlyLogs.map(log => (
            <div key={log.id} className="bg-[#1c1c1e] rounded-2xl border border-gray-800 overflow-hidden shadow-xl">
              <div className="bg-[#2c2c2e] px-6 py-3 border-b border-gray-800 flex justify-between items-center">
                <span className="text-orange-500 font-bold">{new Date(log.date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}</span>
              </div>
              <div className="p-6 space-y-4">
                {log.note && (
                  <div>
                    <div className="flex items-center gap-2 text-gray-400 text-sm mb-2 font-bold">
                      <MessageSquare size={16} className="text-green-500" />
                      {t('impression_label')}
                    </div>
                    <p className="text-white whitespace-pre-wrap leading-relaxed bg-green-500/5 p-4 rounded-xl border border-green-500/50">
                      {log.note}
                    </p>
                  </div>
                )}
                {log.ai_advice && (
                  <div>
                    <div className="flex items-center gap-2 text-gray-400 text-sm mb-2 font-bold">
                      <Bot size={16} className="text-orange-500" />
                      {t('ai_advice_label')}
                    </div>
                    <p className="text-white whitespace-pre-wrap leading-relaxed bg-orange-500/5 p-4 rounded-xl border border-orange-500/20 italic">
                      {log.ai_advice}
                    </p>
                    {log.ai_coach_messages && log.ai_coach_messages.length > 0 && (
                      <div className="mt-3 space-y-3">
                        {log.ai_coach_messages.map(m => (
                          <div
                            key={m.id}
                            className={m.role === 'user'
                              ? 'ml-8 bg-orange-500/10 border border-orange-500/40 p-3 rounded-lg text-gray-200 text-sm whitespace-pre-wrap'
                              : 'mr-8 bg-[#2c2c2e] border border-gray-700 p-3 rounded-lg text-gray-200 text-sm whitespace-pre-wrap leading-relaxed'}
                          >
                            {m.content}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-20 text-gray-500 bg-[#1c1c1e] rounded-2xl border border-gray-800 border-dashed">
            <p className="text-lg">{t('no_reflections_found')}</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default WorkoutReflections
